import type { useUserDirectory } from "../../controllers/useUserDirectory";

/** The resolved-user directory the caller (a screen) already holds; these are pure renderers. */
export type UserDir = ReturnType<typeof useUserDirectory>;

const shortId = (id: string) => `${id.slice(0, 8)}…`;

/** Avatar glyph: two latin letters, else the first (hangul/CJK) character. */
export function initials(name: string): string {
  const t = name.trim();
  if (!t) return "?";
  return /[A-Za-z]/.test(t[0]) ? t.slice(0, 2).toUpperCase() : t[0];
}

/** Why this cell has no name, in the user's words. `pending` says nothing — it's about to resolve. */
const UNRESOLVED_REASON: Record<string, string> = {
  unknown: "확인할 수 없는 사용자",
  failed: "이름 조회 실패",
  pending: "",
};

/**
 * The standard identity cell for member tables: avatar + 이름 on line 1, 소속·이메일 on line 2.
 * Unresolved ids degrade to a muted short id (never a bare UUID) plus the REASON they are
 * unresolved — a deleted/out-of-scope user and a broken lookup call must not look identical.
 */
export function UserCell({ id, dir, fallback }: { id: string; dir: UserDir; fallback?: string }) {
  const v = dir.get(id);
  const status = dir.statusOf(id);
  const name = v?.displayName || v?.email || fallback || shortId(id);
  const sub = v
    ? [v.affiliation, v.displayName ? v.email : null].filter(Boolean).join(" · ")
    // An explicit fallback means the caller already knows why there's no name (e.g. 초대 대기 중),
    // so don't contradict it with a generic reason.
    : fallback ? "" : UNRESOLVED_REASON[status] ?? "";
  // Tooltip carries the human descriptor, never the raw id — showing the UUID on hover would
  // reintroduce exactly what this cell exists to hide.
  return (
    <span className="user-cell" title={v ? dir.detail(id) : UNRESOLVED_REASON[status] || "이름을 불러오는 중…"}>
      <span className="avatar sm">{v ? initials(name) : "?"}</span>
      <span className="user-cell-body">
        <b className={v ? undefined : "unknown"}>{name}</b>
        {sub && <span className="user-cell-sub">{sub}</span>}
      </span>
    </span>
  );
}

/**
 * Banner for a directory-wide lookup failure. Renders nothing when the lookup is healthy, so
 * screens can drop it in unconditionally above any member list.
 */
export function UserDirError({ dir }: { dir: UserDir }) {
  if (!dir.error) return null;
  return (
    <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}>
      <b style={{ color: "var(--amber)" }}>이름을 불러오지 못했습니다:</b> {dir.error}
      {" "}일부 항목이 이름 대신 짧은 ID로 표시됩니다.{" "}
      <button className="link-btn" onClick={dir.retry}>다시 시도</button>
    </div>
  );
}

/** Single-line 이름 for inline spots (댓글 작성자, 리포터, 팝오버); hovers the fuller descriptor. */
export function UserInline({ id, dir }: { id: string; dir: UserDir }) {
  return <span title={dir.detail(id)}>{dir.label(id)}</span>;
}

/** Matches a legacy `@<uuid>` mention token stored in older comment bodies. */
export const MENTION_ID_RE = /@([0-9a-fA-F-]{36})/g;

/**
 * Rewrites legacy `@<uuid>` mention tokens in a stored body into `@이름` so old comments read like
 * new ones. Unresolvable ids keep their short form rather than a full UUID.
 */
export function humanizeMentions(body: string, dir: UserDir): string {
  return body.replace(MENTION_ID_RE, (_m, id: string) => `@${dir.label(id) || shortId(id)}`);
}

/** Plain-string "이름 (이메일)" for <option> labels, falling back to a short id. */
export function userOptionLabel(id: string, dir: UserDir): string {
  const v = dir.get(id);
  if (!v) return shortId(id);
  if (v.displayName && v.email) return `${v.displayName} (${v.email})`;
  return v.displayName || v.email || shortId(id);
}
